<template>
  <div class="container transition-all duration-300 ease-in-out " :style="containerStyle">
    <div v-if="model.isDefinition">
      {{ $t('schema.schemaPage.onlyEditableInEditorMode') }}
    </div>
    <div v-else class="w-full">
      <!-- Header -->
      <div class="flex justify-center mb-6">
        <div class="flex w-full justify-end xl:w-3/4">
          <div class="w-full xl:w-3/4">
            <EditableHeading :text="model.name" :requireCallbackAfterSave="true"
              :readOnly="peerReview || isPublished || isPendingPublish" @changed="updateSchemeTitle" />
            <div class="flex flex-row items-center flex-wrap">
              <StatusBadge :status="assessmentStatus" />
              <div class="ml-2">
                <RoleBadge :userRole="userRole" :assessmentType="assessmentType" />
              </div>
              <p class="py-1 px-2 text-sm text-gray-300">
                {{ $t('schema.common.save.lastSaved') }}: {{ lastSaved }}
              </p>
            </div>

            <div v-if="model.document?.url" class="m-4">
              <EPiLink :url="model.document.url">
                <font-awesome-icon class="float-left mr-4 text-xl" icon="file-pdf" />
                <span class="text-lg">
                  {{ $t('schema.schemaPage.downloadMethodEvaluation') }}
                </span>
              </EPiLink>
            </div>

            <div class="flex flex-wrap my-4">
              <div v-if="schema.organization" class="w-1/2">
                <p v-if="this.model.relatedMunicipalities" class="py-1 text-sm text-gray-300">
                  <span>
                    {{ $t('schema.schemaPage.roles.municipality') }}
                  </span>
                </p>
                <div class="text-lg" v-for="(item, index) in this.model.relatedMunicipalities" :key="$uuid(index)">
                  {{ item.name }}
                </div>
                <p :class="{ 'mt-3': model.relatedMunicipalities?.length && model.relatedOrganizations?.length }"
                  class="py-1 text-sm text-gray-300">
                  <span v-if="this.model.relatedOrganizations">
                    {{ $t('schema.schemaPage.roles.organization') }}
                  </span>
                </p>
                <div class="text-lg" v-for="(item, index) in this.model.relatedOrganizations" :key="$uuid(index)">
                  {{ item.name }}
                </div>
              </div>

              <div v-if="schema.initiator" class="w-1/2">
                <p class="py-1 text-sm text-gray-300">
                  <span v-if="schema.assessmentType === '2' || schema.assessmentType === '3'">
                    {{ $t('schema.schemaPage.roles.initiator') }}
                  </span>
                  <span v-else>
                    {{ $t('schema.schemaPage.roles.initiator') }}
                  </span>
                </p>
                <p class="text-lg">{{ schema.initiator }}</p>
              </div>

              <!-- Contributor & contact area -->
              <div v-show="!model.isPublished" class="flex flex-row flex-wrap items-center space-x-5 w-full mt-6">
                <Invite v-if="userRole === 'initiator'" :roles="model.schema.contributorRoles"
                  :contributors="contributors" :pageId="model.schema.contentGuid" :organizations="model.organizations"
                  :municipalityOptions="model.municipalityOptions" :organizationOptions="model.organizationOptions"
                  :assessmentType="model.assessmentType" @update="getSchema" />

                <button class="flex items-center text-sm cursor-pointer text-lightblue-300 group">
                  <a :href="contactAddress">
                    <font-awesome-icon class="mr-1" icon="envelope" />
                    <span class="text-left underline group-hover:no-underline">Kontakt oss</span>
                  </a>
                </button>

                <PreviewLink :url="model.previewUrl" />
              </div>
            </div>
          </div>
        </div>
      </div>


      <!-- Content Area -->
      <div v-if="schema.schemaItems && schema.schemaItems.length > 0" class="articlebg py-12">
        <!-- Tabs Navigation -->
        <div>
          <div class="xl:hidden mb-6">
              <p>Innholdsmeny:</p>
              <select v-model="openTab" @change="toggleTabs(Number(openTab))"
                class="border border-gray-300 rounded-md p-2 text-lg h-12">
                <option v-for="(tab, index) in tabs" :key="tab.value" :value="index">{{ index + 1 }}. {{ tab.text }}</option>
              </select>
              <!--Sub level-->
            <div class="flex flex-row gap-x-2 ml-2 items-center">
                <font-awesome-icon icon="level-up-alt" :rotation="90" class="text-lightblue-300 mb-5"/>
                <select v-if="sectionMenuItems" class="border border-gray-300 rounded-md p-2 text-lg border-t-0 rounded-t-none h-12"
                        @change="toggleSections(sectionMenuItems[$event.target.selectedIndex].contentGuid)">
                  <option v-for="(sub, index) in sectionMenuItems" :key="sub.value" :value="index" :selected="index === 0">{{ sub.title }}</option>
                </select>
            </div>

          </div>
        </div>
        <div class="flex flex-col xl:flex-row">

          <aside class="hidden xl:block w-1/3 mb-4 lg:pl-8 pr-10">

            <div class="sticky top-8 bg-white border border-gray-100 rounded-md">
              <nav aria-label="Områder">
                <ul>
                  <li v-for="(tab, index) in tabs" :key="tab.value">
                    <button type="button" @click.prevent="toggleTabs(index)"
                      :aria-current="openTab === index ? 'page' : null"
                      class="w-full text-left text-xl p-5 border flex gap-3" :class="[
                        openTab === index
                          ? 'bg-bluedark-100 border-bluedark-100 text-white'
                          : 'border-gray-100 bg-white',
                        index === 0 ? 'rounded-t-md' : ''
                      ]">
                      <div class="flex justify-center items-center rounded-full border-2 w-8 h-8"
                        :class="[openTab === index ? 'border-white' : 'border-gray-200']">
                        <span class="text-lg font-semibold text-gray-700 block mt-1.5">{{ index + 1 }}</span>
                      </div>
                      <span class="block mt-1.5">
                        {{ tab.text }}
                      </span>
                    </button>
                    <div v-if="openTab === index && sectionMenuItems.length > 0"
                      class="py-4 bg-graylight-200 border border-gray-100">
                      <SectionMenu :sections="sectionMenuItems" :openSection="openSection"
                        @toggleSections="toggleSections($event)" :level="index + 1" />
                    </div>
                  </li>
                </ul>
              </nav>
            </div>
          </aside>

          <!-- Right content -->
          <div class="w-full xl:w-3/4 border px-10 pt-10 rounded-md bg-white">
            <ul>
              <li v-for="schemaItem in schema.schemaItems" :key="schemaItem.contentGuid">
                <!-- Section -->
                <template v-if="
                  schemaItem.fieldGroup === openTab &&
                  (schemaItem.blockType.toString().toLowerCase() === 'section' ||
                    schemaItem.blockType.toString().toLowerCase() === '0')
                ">
                  <Section v-if="schemaItem.contentGuid === openSection" :model="schemaItem"
                    :pageId="model.schema.contentGuid" :readOnly="peerReview || isPublished || isPendingPublish"
                    :isPublished="isPublished" @on-save="onInputSave" @get-schema="getSchema"
                    @comment-pane-pinned-change="onCommentPanePinnedChange" :noBorderLayout="noBorderLayout" />
                </template>


                <!-- Peer Review -->
                <template v-if="
                  schemaItem.fieldGroup === openTab &&
                  (schemaItem.blockType.toString().toLowerCase() === 'peerreviewsection' ||
                    schemaItem.blockType.toString().toLowerCase() === '1')
                ">
                  <PeerReviewSection v-if="schemaItem.contentGuid === openSection && !hasPeerReviewsV2"
                    :model="schemaItem" :pageId="model.schema.contentGuid"
                    :readOnly="isPublished || !peerReview || isPendingPublish" :title="model.name"
                    :organization="schema.organization" :isPublished="isPublished" :isPeerReviewed="isPeerReviewed"
                    :isPeerReview="peerReview" @on-save="onInputSave" @update="getSchema" />
                  <PeerReviewSectionV2 v-if="schemaItem.contentGuid === openSection && hasPeerReviewsV2" :model="model"
                    :pageId="model.schema.contentGuid" :reviews="model.schema.peerreviews"
                    :isPeerReviewed="isPeerReviewed" :readOnly="isPublished || !peerReview || isPendingPublish"
                    :title="model.name" :organization="schema.organization" :isPublished="isPublished"
                    :isPeerReview="peerReview" @on-save="onInputSave" @update="getSchema" />
                </template>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!isPublished && userRole === 'initiator'" class="bottom-0 left-0 w-full bg-white py-10">
      <div class="container flex justify-between pr-4">
        <div class="flex items-center text-sm text-gray-500">
          <span>
            {{ $t('schema.common.save.lastSaved') }}:
            <span class="font-medium text-gray-800 ml-1">{{ lastSaved }}</span>
          </span>
        </div>
        <div class="flex items-center">
          <Publish :model="model" :pageId="model.schema.contentGuid"
            :readOnly="peerReview || isPublished || isPendingPublish" :userRole="userRole" :status="assessmentStatus"
            @update="getSchema" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StatusBadge from '@/views/components/schema/badge/StatusBadge.vue';
import RoleBadge from '@/views/components/schema/badge/RoleBadge.vue';
import EPiLink from '@/components/EpiLink.vue';
import Section from '@/views/components/schema/Section.vue';
import SectionMenu from '@/views/components/schema/SectionMenu.vue';
import SectionNavigation from '@/views/components/schema/SectionNavigation.vue';
import PeerReviewSection from '@/views/components/schema/peerReview/PeerReviewSection.vue';
import PeerReviewSectionV2 from '@/views/components/schema/peerReview/PeerReviewSectionV2.vue';
import Invite from '@/views/components/schema/contributor/Invite.vue';
import PreviewLink from '@/views/components/schema/preview/PreviewLink.vue';
import Publish from '@/views/components/schema/publish/Publish.vue';

export default {
  props: ['model'],
  components: {
    StatusBadge,
    RoleBadge,
    EPiLink,
    Section,
    SectionMenu,
    SectionNavigation,
    PeerReviewSection,
    PeerReviewSectionV2,
    Invite,
    PreviewLink,
    Publish,
  },
  data() {
    return {
      assessmentStatus: 1,
      isPendingPublish: false,
      isPublished: false,
      lastSaved: new Date(this.model.changed).toLocaleString(),
      openTab: 0,
      openSection: '',
      schema: this.model.schema,
      contactAddress: '',
      sectionMenuItems: [],
      userRole: '',
      userId: '',
      tabs: [],
      peerReview: false,
      isPeerReviewed: false,
      hasPeerReviewsV2: false,
      contributors: [],
      // organizationList: this.model.relatedOrganizations ? this.model.relatedOrganizations.join(', ') : '',
      // municipalityList: this.model.relatedMunicipalities ? this.model.relatedMunicipalities.join(', ') : '',
      assessmentType: this.model.schema.assessmentType,
      screenWidth: 0,
      isCommentPanePinned: false,
      noBorderLayout: this.model.schema.noBorderLayout || false,
    };
  },
  mounted() {
    const params = new URLSearchParams(window.location.search);
    this.userRole = params.has('role') ? params.get('role').toLowerCase() : '';
    this.userId = params.has('id') ? params.get('id') : '';

    // prepare tabs (from model.schema which is available on this page)
    this.setupTabs();

    // determine URL params presence
    const hasTabParam = params.has('tab');
    const hasSectionParam = params.has('section');

    // find a primary section on the schema (if any)
    const primary = this.model.schema?.schemaItems?.find(si => si.isPrimarySection === true);

    // Priority: URL params > primary section > fallback to defaults
    if (hasTabParam && hasSectionParam) {
      this.openTab = Number(params.get('tab'));
      this.openSection = params.get('section');
    } else if (primary && this.model.isPublished) {
      this.openTab = primary.fieldGroup;
      this.openSection = primary.contentGuid;
    } else {
      this.openTab = hasTabParam ? Number(params.get('tab')) : 0;
      this.openSection = hasSectionParam ? params.get('section') : this.getFirstSectionId(this.openTab);
    }

    // ensure the left menu matches the currently chosen tab
    this.setSectionMenuItems();

    // keep expandedTabs state consistent
    this.expandedTabs = [];
    this.expandedTabs[this.openTab] = true;

    // apply other initial state and start background updates like before
    this.assessmentStatus = this.model.assessmentStatus;
    this.isPendingPublish = this.model.isPendingPublish;
    this.isPublished = this.model.isPublished;
    this.contributors = this.model.schema.contributors;
    this.setMetaTags();
    this.getContactAddress();

    // keep screen width tracking
    this.updateScreenWidth();
    window.addEventListener('resize', this.updateScreenWidth);

    // update URL to reflect the chosen tab/section (so links/bookmarks are consistent)
    this.pushState();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateScreenWidth);
  },
  computed: {
    containerStyle() {
      if (!this.isCommentPanePinned || this.screenWidth < 1280) {
        return {};
      }
      const overlapThreshold = 2320;
      if (this.screenWidth > overlapThreshold) {
        return {
          'max-width': '1530px'
        };
      } else {
        return {
          'max-width': 'min(1530px, calc(100% - 460px))',
          'margin-right': '440px' // This overrides Tailwind's mx-auto
        };
      }
    }
  },
  methods: {
    getFirstSectionId(index) {
      const items = this.model.schema.schemaItems?.filter((item) =>
        item.showInLeftMenu && item.fieldGroup === index
      );
      return items?.length > 0 ? items[0].contentGuid : null;
    },
    setSectionMenuItems() {
      this.sectionMenuItems = this.model.schema.schemaItems.filter(
        (item) => item.showInLeftMenu && item.name.toLowerCase() != "publisering" && item.fieldGroup === this.openTab
      );
    },
    onInputSave() {
      fetch(`${window.location.origin}/api/schema/GetLastSavedSchemaPage?id=${this.model.contentLink.guidValue}`)
        .then((response) => response.json())
        .then((data) => {
          this.lastSaved = new Date(data).toLocaleString();
        });
    },
    toggleTabs(index) {
      this.getSchema();
      this.openTab = index;
      this.openSection = this.getFirstSectionId(index);
      this.setSectionMenuItems();
      this.pushState();


      this.$nextTick(() => {
        window.scrollTo({ top: 450, behavior: 'smooth' });
      });
    },
    toggleSections(id) {
      window.scrollTo({ top: 450, behavior: 'smooth' });
      this.getSchema();
      this.openSection = id;
      this.pushState();
    },
    pushState() {
      const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?tab=${this.openTab}&section=${this.openSection}&role=${this.userRole}&id=${this.userId}`;
      window.history.pushState({ path: newurl }, '', newurl);
    },
    updateSchemeTitle(payload) {
      const path = `${window.location.origin}/api/schema/SaveSchemaBaseProp`;
      const body = JSON.stringify({
        id: this.model.contentLink.guidValue,
        type: 'heading',
        value: payload.text,
      });
      fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body }).then((res) => {
        if (res.status === 200) {
          payload.callback({ success: true });
          this.$toast.open({ message: 'Tittel er oppdatert', type: 'success', dismissible: true });
        } else {
          payload.callback({ success: false });
        }
      });
    },
    getSchema() {
      const id = this.model.contentLink.id;
      fetch(`${window.location.origin}/api/schema/GetMethodAssessmentDetails/${id}`)
        .then((res) => res.json())
        .then((data) => {
          this.schema = data;
          this.assessmentStatus = data.assessmentStatus;
          this.isPendingPublish = data.isPendingPublish;
          this.isPublished = data.isPublished;
          this.isPeerReviewed = data.isPeerReviewed;
          this.hasPeerReviewsV2 = data.hasPeerReviewsV2;
          this.contributors = data.contributors;
        });
    },
    setupTabs() {
      this.peerReview = this.userRole === 'peer';
      this.tabs = this.model.schema.fieldGroups;
    },
    setMetaTags() {
      document.title = this.model.name;
      document.querySelector('meta[property="og:title"]').setAttribute('content', this.model.name);
      const noindex = document.createElement('meta');
      noindex.setAttribute('name', 'robots');
      noindex.setAttribute('content', 'noindex');
      document.head.appendChild(noindex);
    },
    getContactAddress() {
      fetch(`${window.location.origin}/api/schema/GetContactAddress`)
        .then((res) => res.json())
        .then((data) => {
          this.contactAddress = `mailto:${data}`;
        });
    },
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },
    onCommentPanePinnedChange({ isPinned }) {
      this.isCommentPanePinned = isPinned;
    },
  },
};
</script>
